import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@itaas/core/auth/useAuth";
import { DashboardKpiCard } from "@/components/dashboard/DashboardKpiCard";
import { DashboardNav } from "@/components/dashboard/DashboardNav";
import { LowStockAlertsPanel } from "@/components/dashboard/LowStockAlertsPanel";
import { SalesTrendChart } from "@/components/dashboard/SalesTrendChart";
import { Skeleton } from "@/components/skeleton/Skeleton";
import { listAdmissions } from "@/core/admissions/admission-store";
import { ADMISSION_STATUS_LABELS } from "@/core/admissions/types";
import {
  listPharmacyQueuePrescriptions,
  pharmacyOrderPath,
} from "@/core/clinical/clinical-store";
import { PRESCRIPTION_STATUS_LABELS } from "@/core/clinical/types";
import { getPendingDiagnosticsStats } from "@/core/imaging/imaging-store";
import { getTodayAdmissionStats } from "@/core/admissions/admission-store";
import { countCashierQueue } from "@/core/billing/billing-documents/billing-documents-store";
import {
  countOccupiedBeds,
  listBedsWithLocation,
} from "@/core/hospitalization/hospitalization-store";
import { pharmacyInventoryApi } from "@/core/inventory/inventory-api";
import { notifyPharmacyLowStock } from "@/core/notifications/pharmacy-notify";
import { IscApiError } from "@/core/inventory/isc-client";
import {
  buildDashboardAnalytics,
  computeLowStockAlerts,
  estimateStockValue,
  type LowStockAlert,
  type StockValuation,
} from "@/core/pharmacy/dashboard-analytics";
import { getDashboardStats, listValidatedInvoices } from "@/core/pharmacy/pharmacy-store";
import { formatMoney } from "@/core/pharmacy/format";
import { countPendingCases } from "@/core/insurance/coverage-case-store";
import { getPatientById } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import {
  canAccessAdmissionsSection,
  canAccessAdminSection,
  canAccessBillingSection,
  canAccessFinancialDashboard,
  canAccessHospitalizationSection,
  canAccessInsuranceSection,
  canAccessOperationalDashboard,
  canAccessPharmacyModule,
} from "@/core/permissions";
import { routes } from "@/router/all_routes";
import { HospitolSetupStatusBanner } from "@/components/setup/HospitolSetupStatusBanner";
import { useSetupStatus } from "@/core/setup/useSetupStatus";

function IconUsers() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconEmergency() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconLab() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M9 3h6v4H9V3zM4 9h16v12H4V9zM12 12v4M10 14h4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconPrescription() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM14 2v6h6M9 13h6M9 17h4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconBed() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M2 9V19a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V9M2 9V7a2 2 0 0 1 2-2h6v4M2 9h20M10 5h8a2 2 0 0 1 2 2v2"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconCashier() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M2 10h20M6 15h.01M10 15h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function isToday(iso: string): boolean {
  return iso.slice(0, 10) === new Date().toISOString().slice(0, 10);
}

interface TodoItem {
  id: string;
  label: string;
  count: number;
  to: string;
}

export function PharmacyDashboardPage() {
  const auth = useAuth();
  const showInsurance = canAccessInsuranceSection(auth);
  const showAdmissions = canAccessAdmissionsSection(auth);
  const showPharmacy = canAccessPharmacyModule(auth);
  const showHospitalization = canAccessHospitalizationSection(auth);
  const showBilling = canAccessBillingSection(auth);
  const showAdmin = canAccessAdminSection(auth);
  const showFinanceDash = canAccessFinancialDashboard(auth);
  const showOpsDash = canAccessOperationalDashboard(auth);
  const setupStatus = useSetupStatus();

  const pendingInsuranceCases = showInsurance ? countPendingCases() : 0;
  const stats = getDashboardStats();
  const todayFlow = getTodayAdmissionStats();
  const diagnostics = getPendingDiagnosticsStats();
  const invoices = listValidatedInvoices();
  const analytics = useMemo(() => buildDashboardAnalytics(invoices), [invoices.length]);

  const [refreshKey, setRefreshKey] = useState(0);
  const [stockValuation, setStockValuation] = useState<StockValuation>({
    totalValue: 0,
    pricedSkuCount: 0,
    totalUnits: 0,
    loading: true,
    offline: false,
  });
  const [lowStockAlerts, setLowStockAlerts] = useState<LowStockAlert[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { syncPatientsFromApi } = await import("@/core/patients/patient-store");
      const { syncPrescriptionsFromApi } = await import("@/core/clinical/clinical-store");
      const { syncPharmacyInvoicesFromApi } = await import("@/core/pharmacy/pharmacy-store");
      await syncPatientsFromApi();
      await syncPrescriptionsFromApi();
      await syncPharmacyInvoicesFromApi();
      if (!cancelled) setRefreshKey((k) => k + 1);
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  const [stockAlertsLoading, setStockAlertsLoading] = useState(true);
  const [stockAlertsOffline, setStockAlertsOffline] = useState(false);

  useEffect(() => {
    const refresh = () => setRefreshKey((k) => k + 1);
    window.addEventListener("hospitol:admissions", refresh);
    window.addEventListener("hospitol:clinical-prescriptions", refresh);
    window.addEventListener("hospitol:billing-documents", refresh);
    window.addEventListener("hospitol:hospitalization", refresh);
    return () => {
      window.removeEventListener("hospitol:admissions", refresh);
      window.removeEventListener("hospitol:clinical-prescriptions", refresh);
      window.removeEventListener("hospitol:billing-documents", refresh);
      window.removeEventListener("hospitol:hospitalization", refresh);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [balances, products, globalMin] = await Promise.all([
          pharmacyInventoryApi.listPharmacyStockBalances(),
          pharmacyInventoryApi.listPharmacyProducts(),
          pharmacyInventoryApi.getGlobalMinThreshold(),
        ]);
        if (cancelled) return;
        const estimate = estimateStockValue(balances);
        const alerts = computeLowStockAlerts(products, balances, globalMin);
        setStockValuation({ ...estimate, loading: false, offline: false });
        setLowStockAlerts(alerts);
        setStockAlertsLoading(false);
        setStockAlertsOffline(false);

        if (alerts.length > 0) {
          const today = new Date().toISOString().slice(0, 10);
          const notifyKey = `hospitol.lowStockNotify.${today}`;
          if (!localStorage.getItem(notifyKey)) {
            void notifyPharmacyLowStock({
              alerts: alerts.map((a) => ({
                productId: a.productId,
                label: a.label,
                available: a.quantity,
                threshold: a.minThreshold,
              })),
            });
            localStorage.setItem(notifyKey, "1");
          }
        }
      } catch (err) {
        if (cancelled) return;
        setStockValuation({
          ...estimateStockValue([]),
          loading: false,
          offline: err instanceof IscApiError,
        });
        setLowStockAlerts([]);
        setStockAlertsLoading(false);
        setStockAlertsOffline(err instanceof IscApiError);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const pharmacyQueue = useMemo(() => {
    void refreshKey;
    return listPharmacyQueuePrescriptions();
  }, [refreshKey]);

  const activeAdmissions = useMemo(() => {
    void refreshKey;
    return listAdmissions()
      .filter(
        (a) =>
          isToday(a.createdAt) &&
          (a.status === "waiting" || a.status === "in_progress" || a.status === "pending_billing"),
      )
      .slice(0, 5);
  }, [refreshKey]);

  const occupiedBeds = useMemo(() => {
    void refreshKey;
    return countOccupiedBeds();
  }, [refreshKey]);

  const totalBeds = useMemo(() => listBedsWithLocation().length, [refreshKey]);

  const cashierQueue = useMemo(() => {
    void refreshKey;
    return countCashierQueue();
  }, [refreshKey]);

  const todoItems = useMemo(() => {
    const items: TodoItem[] = [];
    if (showAdmissions && (todayFlow.waiting > 0 || todayFlow.inProgress > 0)) {
      items.push({
        id: "admissions",
        label: "Admissions en cours",
        count: todayFlow.waiting + todayFlow.inProgress,
        to: routes.admissions,
      });
    }
    if (showAdmissions && todayFlow.pendingBilling > 0) {
      items.push({
        id: "billing-adm",
        label: "Comptabilité accueil",
        count: todayFlow.pendingBilling,
        to: routes.billingAccounting,
      });
    }
    if (diagnostics.labPending > 0) {
      items.push({
        id: "lab",
        label: "Analyses labo",
        count: diagnostics.labPending,
        to: routes.laboratory,
      });
    }
    if (diagnostics.imagingPending > 0) {
      items.push({
        id: "imaging",
        label: "Examens imagerie",
        count: diagnostics.imagingPending,
        to: routes.imaging,
      });
    }
    if (showPharmacy && pharmacyQueue.length > 0) {
      items.push({
        id: "pharmacy",
        label: "Ordonnances pharmacie",
        count: pharmacyQueue.length,
        to: routes.pharmacyOrders,
      });
    }
    if (showBilling && cashierQueue > 0) {
      items.push({
        id: "cashier",
        label: "File caisse",
        count: cashierQueue,
        to: routes.billingCashier,
      });
    }
    if (lowStockAlerts.length > 0) {
      items.push({
        id: "stock",
        label: "Stock bas",
        count: lowStockAlerts.length,
        to: routes.supplyLevels,
      });
    }
    if (showInsurance && pendingInsuranceCases > 0) {
      items.push({
        id: "insurance",
        label: "Assurance",
        count: pendingInsuranceCases,
        to: routes.insuranceCases,
      });
    }
    return items;
  }, [
    showAdmissions,
    showPharmacy,
    showBilling,
    showInsurance,
    todayFlow,
    diagnostics,
    pharmacyQueue.length,
    cashierQueue,
    lowStockAlerts.length,
    pendingInsuranceCases,
  ]);

  const recent = invoices.slice(0, 6);
  const todayLabel = new Date().toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <div className="ph-dashboard">
      {showAdmin && <HospitolSetupStatusBanner status={setupStatus.status} />}
      <section className="ph-dashboard-hero ph-dashboard-hero--compact">
        <div className="ph-dashboard-hero-content">
          <p className="ph-dashboard-hero-kicker">Vue exécutive — ITaaS Hospitol</p>
          <p className="ph-dashboard-hero-date mb-0">{todayLabel}</p>
        </div>
        <div className="ph-dashboard-hero-actions">
          {(showFinanceDash || showOpsDash) && (
            <DashboardNav showFinance={showFinanceDash} showOperations={showOpsDash} />
          )}
          {showAdmin && setupStatus.status && !setupStatus.status.complete && (
            <Link to={routes.hospitolSetup} className="btn btn-warning btn-sm fw-semibold shadow-sm">
              Mise en route
            </Link>
          )}
          {showAdmissions && (
            <Link to={routes.admissions} className="btn btn-light btn-sm fw-semibold shadow-sm">
              Admissions
            </Link>
          )}
          {showPharmacy && (
            <Link to={routes.pharmacyInvoice} className="btn btn-outline-light btn-sm">
              + Nouvelle facture
            </Link>
          )}
          {showPharmacy && (
            <Link to={routes.pharmacyOrders} className="btn btn-outline-light btn-sm">
              Ordonnances
            </Link>
          )}
        </div>
      </section>

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-4 col-xl">
          <DashboardKpiCard
            label="Patients du jour"
            value={String(todayFlow.totalPatients)}
            hint={`${todayFlow.admissions} admission(s) · ${todayFlow.emergency} urgence(s)`}
            icon={<IconUsers />}
            accent="teal"
          />
        </div>
        {showAdmissions && (
          <div className="col-6 col-md-4 col-xl">
            <Link to={routes.admissions} className="text-decoration-none d-block h-100">
              <DashboardKpiCard
                label="Admissions en attente"
                value={String(todayFlow.waiting + todayFlow.inProgress)}
                hint={
                  todayFlow.pendingBilling > 0
                    ? `${todayFlow.pendingBilling} en comptabilité`
                    : "Accueil & orientation"
                }
                icon={<IconUsers />}
                accent="amber"
              />
            </Link>
          </div>
        )}
        {showAdmissions && (
          <div className="col-6 col-md-4 col-xl">
            <Link to={routes.emergency} className="text-decoration-none d-block h-100">
              <DashboardKpiCard
                label="Urgences du jour"
                value={String(todayFlow.emergency)}
                hint="Passages triés P1 → P3"
                icon={<IconEmergency />}
                accent="violet"
              />
            </Link>
          </div>
        )}
        <div className="col-6 col-md-4 col-xl">
          <DashboardKpiCard
            label="Examens en attente"
            value={String(diagnostics.total)}
            hint={`Labo : ${diagnostics.labPending} · Imagerie : ${diagnostics.imagingPending}`}
            icon={<IconLab />}
            accent="violet"
          />
        </div>
        {showPharmacy && (
          <div className="col-6 col-md-4 col-xl">
            <Link to={routes.pharmacyOrders} className="text-decoration-none d-block h-100">
              <DashboardKpiCard
                label="Ordonnances en file"
                value={String(pharmacyQueue.length)}
                hint="À dispenser en officine"
                icon={<IconPrescription />}
                accent="teal"
              />
            </Link>
          </div>
        )}
        {showHospitalization && (
          <div className="col-6 col-md-4 col-xl">
            <Link to={routes.hospitalization} className="text-decoration-none d-block h-100">
              <DashboardKpiCard
                label="Lits occupés"
                value={`${occupiedBeds}/${totalBeds}`}
                hint="Hospitalisation en cours"
                icon={<IconBed />}
                accent="emerald"
              />
            </Link>
          </div>
        )}
        {showBilling && (
          <div className="col-6 col-md-4 col-xl">
            <Link to={routes.billingCashier} className="text-decoration-none d-block h-100">
              <DashboardKpiCard
                label="File caisse"
                value={String(cashierQueue)}
                hint="Documents à encaisser"
                icon={<IconCashier />}
                accent="amber"
              />
            </Link>
          </div>
        )}
      </div>

      <div className="ph-dash-card ph-dash-todo mb-4">
        <div className="ph-dash-card-head">
          <div>
            <h3 className="ph-dash-card-title">À traiter</h3>
            <p className="ph-dash-card-sub">
              {todoItems.length > 0
                ? `${todoItems.length} file(s) nécessitent votre attention`
                : "Aucune file en attente — flux opérationnel calme"}
            </p>
          </div>
        </div>
        {todoItems.length > 0 ? (
          <div className="ph-dash-todo-grid">
            {todoItems.map((item) => (
              <Link key={item.id} to={item.to} className="ph-dash-todo-chip">
                <span className="ph-dash-todo-count">{item.count}</span>
                <span className="ph-dash-todo-label">{item.label}</span>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-muted small mb-0 px-1">
            Consultez les modules via le menu latéral ou utilisez les actions rapides ci-dessus.
          </p>
        )}
      </div>

      {(showAdmissions || showPharmacy) && (
        <div className="row g-3 mb-4 ph-split-workspace">
          {showAdmissions && (
            <div className={showPharmacy ? "col-lg-6" : "col-12"}>
              <div className="ph-dash-card h-100">
                <div className="ph-dash-card-head">
                  <div>
                    <h3 className="ph-dash-card-title">Flux admissions</h3>
                    <p className="ph-dash-card-sub">En attente, en cours ou comptabilité</p>
                  </div>
                  <Link to={routes.admissions} className="btn btn-sm ph-btn-outline">
                    Voir tout
                  </Link>
                </div>
                {activeAdmissions.length === 0 ? (
                  <p className="text-muted small mb-0">Aucune admission active aujourd&apos;hui.</p>
                ) : (
                  <ul className="ph-dash-mini-list">
                    {activeAdmissions.map((admission) => {
                      const patient = getPatientById(admission.patientId);
                      const name = patient
                        ? patientFullName(patient)
                        : admission.walkInName || "Visiteur";
                      return (
                        <li key={admission.id} className="ph-dash-mini-item">
                          <div>
                            <span className="ph-dash-mini-label">{name}</span>
                            <span className="ph-dash-mini-meta">
                              {admission.channel === "emergency" ? "Urgences" : "Accueil"} ·{" "}
                              {new Date(admission.createdAt).toLocaleTimeString("fr-FR", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                          </div>
                          <span className="ph-dash-mini-badge">
                            {ADMISSION_STATUS_LABELS[admission.status]}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}
          {showPharmacy && (
            <div className={showAdmissions ? "col-lg-6" : "col-12"}>
              <div className="ph-dash-card h-100">
                <div className="ph-dash-card-head">
                  <div>
                    <h3 className="ph-dash-card-title">File pharmacie</h3>
                    <p className="ph-dash-card-sub">Ordonnances à dispenser</p>
                  </div>
                  <Link to={routes.pharmacyOrders} className="btn btn-sm ph-btn-outline">
                    Voir tout
                  </Link>
                </div>
                {pharmacyQueue.length === 0 ? (
                  <p className="text-muted small mb-0">Aucune ordonnance en attente.</p>
                ) : (
                  <ul className="ph-dash-mini-list">
                    {pharmacyQueue.slice(0, 5).map((rx) => {
                      const patient = getPatientById(rx.patientId);
                      const name = patient ? patientFullName(patient) : "Patient inconnu";
                      return (
                        <li key={rx.id} className="ph-dash-mini-item">
                          <div>
                            <Link to={pharmacyOrderPath(rx.id)} className="ph-dash-mini-label">
                              {name}
                            </Link>
                            <span className="ph-dash-mini-meta">
                              {rx.lines.length} ligne(s) · {PRESCRIPTION_STATUS_LABELS[rx.status]}
                            </span>
                          </div>
                          <Link
                            to={pharmacyOrderPath(rx.id)}
                            className="btn btn-sm ph-btn-outline py-0 px-2"
                          >
                            Traiter
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="row g-3 mb-4 ph-split-workspace">
        <div className="col-lg-8">
          <div className="ph-dash-card">
            <div className="ph-dash-card-head">
              <div>
                <h3 className="ph-dash-card-title">Recettes pharmacie</h3>
                <p className="ph-dash-card-sub">Évolution sur les 7 derniers jours</p>
              </div>
              <Link to={routes.billingInvoices} className="btn btn-sm ph-btn-outline">
                Recettes détaillées
              </Link>
            </div>
            <div className="ph-dash-finance-strip mb-3">
              <div className="ph-dash-finance-item">
                <span className="ph-dash-finance-label">Aujourd&apos;hui</span>
                <span className="ph-dash-finance-value">{formatMoney(stats.revenueToday)}</span>
              </div>
              <div className="ph-dash-finance-item">
                <span className="ph-dash-finance-label">Semaine</span>
                <span className="ph-dash-finance-value">{formatMoney(analytics.revenueWeek)}</span>
              </div>
              <div className="ph-dash-finance-item">
                <span className="ph-dash-finance-label">Mois</span>
                <span className="ph-dash-finance-value">{formatMoney(analytics.revenueMonth)}</span>
              </div>
              {showPharmacy && (
                <div className="ph-dash-finance-item">
                  <span className="ph-dash-finance-label">Stock valorisé</span>
                  {stockValuation.loading ? (
                    <Skeleton className="ph-skeleton--kpi-value" style={{ marginTop: "0.2rem" }} />
                  ) : (
                    <span className="ph-dash-finance-value">
                      {stockValuation.offline ? "—" : formatMoney(stockValuation.totalValue)}
                    </span>
                  )}
                </div>
              )}
            </div>
            <SalesTrendChart data={analytics.salesLast7Days} />
          </div>
        </div>
        <div className="col-lg-4">
          <div className="ph-dash-card h-100">
            <LowStockAlertsPanel
              alerts={lowStockAlerts}
              loading={stockAlertsLoading}
              offline={stockAlertsOffline}
              maxItems={5}
            />
          </div>
        </div>
      </div>

      <div className="ph-dash-card">
        <div className="ph-dash-card-head">
          <div>
            <h3 className="ph-dash-card-title">Dernières factures</h3>
            <p className="ph-dash-card-sub">Ventes comptoir récentes</p>
          </div>
          <div className="d-flex gap-2 ph-split-actions">
            <Link to={routes.pharmacyInvoiceHistory} className="btn btn-sm ph-btn-outline">
              Historique
            </Link>
            {showPharmacy && (
              <Link to={routes.pharmacyInvoice} className="btn btn-sm ph-btn-outline">
                Nouvelle vente
              </Link>
            )}
          </div>
        </div>
        {recent.length === 0 ? (
          <div className="p-4 text-muted">
            Aucune facture validée —{" "}
            <Link to={routes.pharmacyInvoice}>créer la première vente</Link>.
          </div>
        ) : (
          <div className="table-responsive ph-diag-table">
            <table className="table table-hover ph-dash-table mb-0">
              <thead>
                <tr>
                  <th>N° facture</th>
                  <th>Client</th>
                  <th>Date</th>
                  <th className="text-end">Montant</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((inv) => (
                  <tr key={inv.id} className="ph-diag-row">
                    <td data-label="N° facture">
                      <span className="ph-inv-badge">{inv.number}</span>
                    </td>
                    <td className="fw-medium" data-label="Client">
                      {inv.customer.name}
                    </td>
                    <td className="text-muted small" data-label="Date">
                      {new Date(inv.validatedAt).toLocaleString("fr-FR")}
                    </td>
                    <td className="text-end fw-semibold text-success" data-label="Montant">
                      {formatMoney(inv.subtotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
